import { appOn, type ChatMessage, type MessagePage } from "@rhema/client";
import {
  Button,
  ButtonLink,
  cn,
  EmptyState,
  HeadphonesIcon,
  IconButton,
  type MessageBubbleProps,
  MessageIcon,
  PhoneIcon,
  SearchIcon,
  Toast,
  TopBar,
  VideoIcon,
} from "@rhema/ui";
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams } from "react-router";
import { ChatBoundary } from "../app/Crash";
import { errorText } from "../billing/UpgradeLink";
import { useCalls } from "../calls/CallProvider";
import { canPost, chatView, firstName, isAdmin } from "../data/view";
import { EmailComposer } from "../links/EmailComposer";
import { useMenus } from "../menus/actions";
import { MoreButton } from "../menus/WithMenu";
import { UnknownSenderBar } from "../people/UnknownSender";
import { useDraftedReply, useQueravelPanel, useQueravelSurface } from "../queravel/panel";
import { useQueravelOffered } from "../queravel/QueravelChat";
import { ChatAvatar } from "../screens/ChatList";
import { useClient, useClientState, useSession } from "../session/session";
import { homeSpaceId } from "../spaces/data";
import { HuddleBar, PinnedBar, PinsSheet, ScheduledBar, ScheduledSheet } from "./ChatBars";
import { keyHint, MessageBox, type MessageBoxHandle } from "./MessageBox";
import { ContentProvider } from "./MessageContent";
import { MessageList, nameIn, textOf } from "./MessageList";
import { useMessageMenu } from "./messageMenu";
import { useChatMedia } from "./useChatMedia";

export type Panel = "info";

/** How long the message a link opened stays picked out. */
const HIGHLIGHT_MS = 2500;

/** Two pages of messages as one, in order. The live copy of a message wins over a page's. */
function mergePages(a: ChatMessage[], b: ChatMessage[]): ChatMessage[] {
  const byId = new Map<string, ChatMessage>();
  for (const m of a) byId.set(m.id, m);
  for (const m of b) byId.set(m.id, m);
  return [...byId.values()].sort(
    (x, y) => (x.seq || Number.MAX_SAFE_INTEGER) - (y.seq || Number.MAX_SAFE_INTEGER),
  );
}

function useToast() {
  const [toast, setToast] = useState<string | null>(null);
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3000);
    return () => clearTimeout(t);
  }, [toast]);
  return [toast, setToast] as const;
}

type ConversationProps = {
  chatId: string;
  variant: "phone" | "desktop";
  panel?: Panel | null;
  onPanel?: (panel: Panel | null) => void;
  /** Opens a message's thread. Channels give it. */
  onThread?: (rootId: string) => void;
};

/** One chat. A failure inside it shows a notice in its place, and the rest of the app keeps working. */
export function Conversation(props: ConversationProps) {
  return (
    <ChatBoundary resetKey={props.chatId}>
      <ConversationScreen {...props} />
    </ChatBoundary>
  );
}

function ConversationScreen({ chatId, variant, panel, onPanel, onThread }: ConversationProps) {
  const client = useClient();
  const { platform } = useSession();
  const state = useClientState();
  const chat = state.chats[chatId];
  const live = useMemo(() => state.messages[chatId] ?? [], [state.messages, chatId]);
  // A link to an older message shows the page around it until the person jumps back to the latest.
  const [around, setAround] = useState<MessagePage | null>(null);
  const messages = useMemo(() => {
    if (!around) return live;
    if (!around.has_after) return mergePages(around.messages, live);
    const last = around.messages[around.messages.length - 1]?.seq ?? 0;
    return mergePages(
      around.messages,
      live.filter((m) => m.seq > 0 && m.seq <= last),
    );
  }, [around, live]);
  const hasOlder = around ? around.has_before : (state.hasOlder[chatId] ?? false);
  const meId = state.me?.id;
  const view = chat ? chatView(chat, state) : null;

  const [params, setParams] = useSearchParams();
  const { spaceId: routeSpaceId } = useParams();
  const [loadError, setLoadError] = useState<string | null>(null);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const [editing, setEditing] = useState<ChatMessage | null>(null);
  const box = useRef<MessageBoxHandle>(null);
  const [toast, setToast] = useToast();
  const [pinsOpen, setPinsOpen] = useState(false);
  const [scheduledOpen, setScheduledOpen] = useState(false);
  const [highlight, setHighlight] = useState<string | null>(null);
  const [linkProblem, setLinkProblem] = useState<string | null>(null);
  const [opened, setOpened] = useState(false);
  // After Mark unread from here the chat stays unread while it is on screen, until a new message.
  const holdRead = useRef(false);
  const calls = useCalls();
  const menus = useMenus();
  const scroller = useRef<HTMLDivElement>(null);
  const atBottom = useRef(true);
  const restoreFrom = useRef<number | null>(null);
  const loadingOlder = useRef(false);
  // Selecting several messages, to forward, copy, star or email them. Ids keep the chat's order.
  const [selected, setSelected] = useState<string[] | null>(null);
  const [emailFor, setEmailFor] = useState<ChatMessage[] | null>(null);
  const queravelOffered = useQueravelOffered();
  const toggleSelected = (id: string) =>
    setSelected((list) => (list?.includes(id) ? list.filter((x) => x !== id) : [...(list ?? []), id]));
  const selectProps: (id: string) => Partial<MessageBubbleProps> = (id) => {
    const on = Boolean(selected?.includes(id));
    const m = messages.find((x) => x.id === id);
    return {
      tabIndex: 0,
      role: "checkbox",
      "aria-checked": on,
      "aria-label": m ? `Select: ${textOf(m)}` : "Select message",
      // Taps choose the message instead of opening the photo or link inside it.
      onClickCapture: (e) => {
        e.preventDefault();
        e.stopPropagation();
        toggleSelected(id);
      },
      onKeyDown: (e) => {
        if (e.target !== e.currentTarget || (e.key !== "Enter" && e.key !== " ")) return;
        e.preventDefault();
        toggleSelected(id);
      },
      style: on ? { outline: "2px solid var(--color-blue)", outlineOffset: 3 } : undefined,
    };
  };
  const desktop = variant === "desktop";
  const sheet = params.get("sheet");

  // Cached messages appear at once. Anything newer is fetched and decrypted. Moving to another chat
  // clears what was typed. The first render needs no clearing, and clearing then could wipe out
  // typing that happened before the effect ran.
  const shownChat = useRef(chatId);
  useEffect(() => {
    setLoadError(null);
    setOpened(false);
    client
      .openChat(chatId)
      .then(() => setOpened(true))
      .catch((e: Error) => {
        setLoadError(e.message);
        setOpened(true);
      });
    if (shownChat.current === chatId) return;
    shownChat.current = chatId;
    setReplyTo(null);
    setEditing(null);
    setSelected(null);
    setAround(null);
    setHighlight(null);
    setLinkProblem(null);
    holdRead.current = false;
    setEmailFor(null);
    setPinsOpen(false);
    setScheduledOpen(false);
    atBottom.current = true;
  }, [client, chatId]);

  // Pins, your scheduled messages and a channel's huddle, which are kept apart from the messages.
  const kind = state.chats[chatId]?.kind;
  const scheduledLoaded = state.scheduled !== null;
  useEffect(() => {
    if (!kind || kind === "assistant") return;
    void client.loadPins(chatId).catch(() => undefined);
    if (kind === "channel") void client.calls.loadHuddle(chatId).catch(() => undefined);
  }, [client, chatId, kind]);
  useEffect(() => {
    if (!scheduledLoaded) void client.loadScheduled().catch(() => undefined);
  }, [client, scheduledLoaded]);

  // Mark as read while the conversation is on screen and the window has focus.
  const lastSeq = live.reduce((n, m) => Math.max(n, m.seq), 0);
  const heldAt = useRef(0);
  useEffect(() => {
    // A new message ends Mark unread from here, as opening the chat again does.
    if (holdRead.current && lastSeq > heldAt.current) holdRead.current = false;
    const read = () => {
      if (holdRead.current) return;
      if (document.visibilityState === "visible" && document.hasFocus())
        void client.markRead(chatId).catch(() => undefined);
    };
    read();
    window.addEventListener("focus", read);
    document.addEventListener("visibilitychange", read);
    return () => {
      window.removeEventListener("focus", read);
      document.removeEventListener("visibilitychange", read);
    };
  }, [client, chatId, lastSeq]);

  // A link to a message, /m/chat/message, opens the chat with ?m=. The message is picked out where
  // it already shows. Otherwise the page around it loads in place of the latest messages.
  const linked = params.get("m");
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs once the chat has opened
  useEffect(() => {
    if (!linked || !opened) return;
    const done = () => {
      const p = new URLSearchParams(params);
      p.delete("m");
      setParams(p, { replace: true });
      setHighlight(linked);
    };
    if (live.some((m) => m.id === linked)) {
      setAround(null);
      done();
      return;
    }
    let current = true;
    client
      .messagePage(chatId, linked)
      .then((page) => {
        if (!current || page.thread_root) return;
        setAround(page);
        done();
      })
      .catch((e: { status?: number; message?: string }) => {
        if (!current) return;
        setLinkProblem(
          e.status === 404
            ? "This message is not available to you. It may have been deleted, or you are not in this chat."
            : e.message || "The message could not be opened.",
        );
        const p = new URLSearchParams(params);
        p.delete("m");
        setParams(p, { replace: true });
      });
    return () => {
      current = false;
    };
  }, [linked, opened, chatId]);

  // The linked message comes into view, and stays picked out for a moment.
  useLayoutEffect(() => {
    if (!highlight) return;
    const el = scroller.current?.querySelector<HTMLElement>(`[data-message-id="${highlight}"]`);
    if (el) {
      atBottom.current = false;
      el.scrollIntoView?.({ block: "center" });
    }
    const t = window.setTimeout(() => setHighlight(null), HIGHLIGHT_MS);
    return () => window.clearTimeout(t);
  }, [highlight]);

  const jumpToLatest = () => {
    setAround(null);
    atBottom.current = true;
  };

  // Keep the view pinned to the newest message, or steady while older ones load above.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the list changes
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (restoreFrom.current !== null) {
      el.scrollTop += el.scrollHeight - restoreFrom.current;
      restoreFrom.current = null;
    } else if (atBottom.current) {
      el.scrollTop = el.scrollHeight;
    }
  }, [messages, state.typing[chatId]]);

  const onScroll = useCallback(() => {
    const el = scroller.current;
    if (!el) return;
    atBottom.current = el.scrollHeight - el.scrollTop - el.clientHeight < 80;
    if (around) {
      // Further pages around the linked message load as the person scrolls toward them.
      const first = around.messages[0];
      const last = around.messages[around.messages.length - 1];
      const up = el.scrollTop < 80 && around.has_before && first;
      const down = atBottom.current && around.has_after && last;
      if (loadingOlder.current || (!up && !down)) return;
      loadingOlder.current = true;
      if (up) restoreFrom.current = el.scrollHeight;
      const from = (up ? first : last) as ChatMessage;
      client
        .messagePage(chatId, from.id)
        .then((page) =>
          setAround((now) =>
            now
              ? {
                  ...now,
                  messages: mergePages(now.messages, page.messages),
                  has_before: up ? page.has_before : now.has_before,
                  has_after: up ? now.has_after : page.has_after,
                }
              : now,
          ),
        )
        .catch(() => {
          restoreFrom.current = null;
        })
        .finally(() => {
          loadingOlder.current = false;
        });
      return;
    }
    if (el.scrollTop < 80 && hasOlder && !loadingOlder.current) {
      loadingOlder.current = true;
      restoreFrom.current = el.scrollHeight;
      client
        .loadOlder(chatId)
        .catch(() => {
          restoreFrom.current = null;
        })
        .finally(() => {
          loadingOlder.current = false;
        });
    }
  }, [client, chatId, hasOlder, around]);

  // Group admins can turn Queravel off for a group, and space admins any app for their channels.
  const peniellyOn = chat ? appOn(state, chat, "penielly") : false;
  const chatMedia = useChatMedia({
    chat,
    penielly: peniellyOn,
    meId,
    messages,
    recipient: view
      ? chat?.kind === "channel"
        ? `#${view.title}`
        : chat?.kind === "group"
          ? view.title
          : firstName(view.title)
      : "",
    attachOpen: sheet === "attach",
    closeAttach: () => {
      const p = new URLSearchParams(params);
      p.delete("sheet");
      setParams(p, { replace: true });
    },
    replyTo,
    onSent: () => {
      atBottom.current = true;
      setReplyTo(null);
    },
    toast: setToast,
  });

  // The Queravel panel reads the chat on screen, and can put a drafted reply in its composer.
  const queravel = useQueravelPanel();
  const surfaceTitle = chat && view ? (chat.kind === "channel" ? `#${view.title}` : view.title) : null;
  useQueravelSurface(
    surfaceTitle !== null && chat?.kind !== "assistant" ? { chatId, title: surfaceTitle } : null,
  );
  useDraftedReply(chat ? chatId : null, (text) => box.current?.fill(text));
  const queravelAllowed = Boolean(chat && queravelOffered && appOn(state, chat, "queravel"));
  // ?sheet=assist, from older links, opens the Queravel panel about this chat.
  // biome-ignore lint/correctness/useExhaustiveDependencies: runs when the link asks for it
  useEffect(() => {
    if (sheet !== "assist" || !queravelAllowed || surfaceTitle === null) return;
    const p = new URLSearchParams(params);
    p.delete("sheet");
    setParams(p, { replace: true });
    queravel.show({ about: { chatId, title: surfaceTitle } });
  }, [sheet, queravelAllowed, surfaceTitle]);

  // Editing puts the message in the box with its formatting and the people it mentions.
  const startEdit = (m: ChatMessage | null) => {
    setEditing(m);
    if (m) setReplyTo(null);
  };
  const titleForAsk = surfaceTitle ?? "";
  const menu = useMessageMenu({
    chatId,
    messages,
    desktop,
    onReply: (m) => {
      setEditing(null);
      setReplyTo(m);
    },
    ...(onThread ? { onThread: (m: ChatMessage) => onThread(m.id) } : {}),
    onEdit: startEdit,
    onSelect: (m) => setSelected([m.id]),
    ...(queravelAllowed
      ? {
          onAsk: (m: ChatMessage) => queravel.show({ about: { chatId, title: titleForAsk, messages: [m] } }),
        }
      : {}),
    ...(chat && appOn(state, chat, "malakh") ? { onEmail: (m: ChatMessage) => setEmailFor([m]) } : {}),
    onMarkedUnread: () => {
      holdRead.current = true;
      heldAt.current = lastSeq;
    },
  });
  const actionProps = selected ? selectProps : menu.holdProps;

  if (linkProblem && !chat) {
    return (
      <EmptyState
        icon={<MessageIcon size={26} />}
        title="This message is not available to you"
        className="flex-1"
      >
        {linkProblem}
      </EmptyState>
    );
  }

  if (!chat || !view) {
    return loadError ? (
      <EmptyState icon={<MessageIcon size={26} />} title="Chat not found" className="flex-1">
        This chat may have been deleted, or you may have left it.
      </EmptyState>
    ) : (
      <EmptyState icon={<MessageIcon size={26} />} title="Opening the chat" className="flex-1" />
    );
  }

  const channel = chat.kind === "channel";
  // A channel works as a group does here, with names, mentions and admins.
  const group = chat.kind === "group" || channel;
  // Your own chat has no one to call. Channels have huddles in place of calls.
  const mine = chat.kind === "self" || channel;
  const pins = state.pins[chatId] ?? [];
  const canPin = !chat.only_admins_post || isAdmin(chat);
  const scheduled = (state.scheduled ?? []).filter((s) => s.chatId === chatId && !s.threadRoot);
  const huddle = channel ? calls.state.huddles[chatId] : undefined;
  const inHuddle = Boolean(huddle && calls.state.active?.call.id === huddle.id);
  const schedulable = chat.kind !== "assistant" && !editing;
  // A shared channel is open in the space in the address, which may not be the one that owns it.
  const spaceId = channel ? routeSpaceId || homeSpaceId(state, chat) : undefined;
  const chatSpace = spaceId ? state.spaces.find((x) => x.id === spaceId) : undefined;
  const title = channel ? `#${view.title}` : view.title;
  const more = (
    <MoreButton label={title} items={() => (channel ? menus.channel(chat, chatSpace) : menus.chat(chat))} />
  );
  const posting = canPost(chat);
  const typing = (state.typing[chatId] ?? []).filter((id) => id !== meId);

  const setSheet = (next: "assist" | "attach" | null) => {
    const p = new URLSearchParams(params);
    if (next) p.set("sheet", next);
    else p.delete("sheet");
    setParams(p, { replace: true });
  };

  // Queravel can be asked here when the server offers it and the chat allows it.
  const askable = queravelAllowed;
  const emailable = appOn(state, chat, "malakh");
  // Every way of asking opens the one Queravel panel. Picked messages go with it.
  const askAbout = (picked?: ChatMessage[]) =>
    queravel.show({ about: { chatId, title, ...(picked ? { messages: picked } : {}) } });
  const askQueravel = askable ? () => askAbout() : undefined;
  const openInfo = () => (desktop ? onPanel?.(panel === "info" ? null : "info") : undefined);

  // Selected messages in the order they were sent.
  const chosen = selected ? messages.filter((m) => selected.includes(m.id)) : [];
  const chosenSavable = chosen.filter((m) => menu.canSaveToPenielly(m));
  const chosenSent = chosen.filter((m) => m.seq > 0);
  const finishSelecting = (job: () => void) => {
    job();
    setSelected(null);
  };

  const subtitle = typing.length
    ? group
      ? `${typing.map((id) => firstName(nameIn(chat, id, meId))).join(" and ")} typing…`
      : "typing…"
    : view.subtitle;

  const header = desktop ? (
    <TopBar
      leading={<ChatAvatar chat={view} size={42} preferInitials />}
      title={title}
      subtitle={subtitle}
      subtitleTone={view.online && !typing.length ? "online" : "default"}
      onTitleClick={openInfo}
      actions={
        <>
          <IconButton label="Search this chat" variant="outline" size={40} href={`/search?chat=${chat.id}`}>
            <SearchIcon size={18} strokeWidth={2.2} />
          </IconButton>
          {channel && !huddle && (
            <ButtonLink
              href={`/calls/${chat.id}?media=voice`}
              size="sm"
              variant="outline"
              icon={<HeadphonesIcon size={17} strokeWidth={2.2} />}
            >
              Huddle
            </ButtonLink>
          )}
          {/* While the panel is open it already reads this chat, so the button gives the title room. */}
          {askQueravel && !queravel.open && (
            <Button
              size="sm"
              variant="outline"
              onClick={askQueravel}
              className="border-queravel bg-queravel-tint text-queravel hover:bg-queravel-tint"
            >
              Ask Queravel
            </Button>
          )}
          {!mine && (
            <>
              <IconButton
                label="Voice call"
                variant="outline"
                size={40}
                href={`/calls/${chat.id}?media=voice`}
              >
                <PhoneIcon size={18} strokeWidth={2.2} />
              </IconButton>
              <ButtonLink
                href={`/calls/${chat.id}?media=video`}
                size="sm"
                icon={<VideoIcon size={17} strokeWidth={2.2} />}
              >
                Video call
              </ButtonLink>
            </>
          )}
          {more}
        </>
      }
    />
  ) : (
    <TopBar
      back={{ href: "/chats", label: "Back to chats" }}
      leading={<ChatAvatar chat={view} size={42} />}
      title={title}
      subtitle={subtitle}
      subtitleTone={view.online && !typing.length ? "online" : "default"}
      titleHref={channel && spaceId ? `/spaces/${spaceId}/${chat.id}/info` : `/chats/${chat.id}/info`}
      actions={
        channel ? (
          <>
            {!huddle && (
              <IconButton label="Start a huddle" href={`/calls/${chat.id}?media=voice`}>
                <HeadphonesIcon size={22} />
              </IconButton>
            )}
            {more}
          </>
        ) : mine ? (
          more
        ) : group ? (
          <>
            <ButtonLink href={`/calls/${chat.id}`} size="sm" icon={<VideoIcon size={17} strokeWidth={2.2} />}>
              Call
            </ButtonLink>
            {more}
          </>
        ) : (
          <>
            <IconButton label="Video call" href={`/calls/${chat.id}?media=video`}>
              <VideoIcon size={23} />
            </IconButton>
            <IconButton label="Voice call" href={`/calls/${chat.id}?media=voice`}>
              <PhoneIcon size={21} />
            </IconButton>
            {more}
          </>
        )
      }
    />
  );

  return (
    <div className="relative flex min-h-0 flex-1 flex-col bg-chat" {...chatMedia.container}>
      {header}
      {huddle && <HuddleBar chat={chat} huddle={huddle} meId={meId} inIt={inHuddle} />}
      <PinnedBar pins={pins} onOpen={() => setPinsOpen(true)} />
      <div
        ref={scroller}
        onScroll={onScroll}
        className={cn(
          "min-h-0 flex-1 overflow-y-auto bg-[radial-gradient(var(--color-chat-dot)_1.1px,transparent_1.1px)]",
          desktop ? "bg-size-[22px_22px]" : "bg-size-[20px_20px]",
        )}
      >
        <div
          role="log"
          aria-label={`Messages with ${view.title}`}
          className={cn(
            "flex min-h-full flex-col justify-end gap-1",
            desktop ? "px-7 py-[18px]" : "px-3.5 pt-3 pb-3.5",
          )}
        >
          {linkProblem && (
            <p
              role="alert"
              className="m-0 mb-2 self-center rounded-field bg-scarlet-tint px-3.5 py-2 text-sm text-scarlet"
            >
              {linkProblem}
            </p>
          )}
          {hasOlder && (
            <p className="m-0 mb-2 self-center text-xs text-ink-meta" role="status">
              Scroll up for earlier messages
            </p>
          )}
          <ContentProvider value={chatMedia.content}>
            <MessageList
              chat={chat}
              messages={messages}
              meId={meId}
              hasOlder={hasOlder}
              typing={typing}
              describe={(s) => client.describeSystem(s, chat)}
              onRetry={(m) =>
                m.clientId &&
                void client.retry(chatId, m.clientId).catch((e) => setToast(errorText(e, platform)))
              }
              {...(desktop && onPanel ? { onNotice: () => onPanel("info") } : {})}
              actionProps={actionProps}
              {...(onThread ? { onThread: (m: ChatMessage) => onThread(m.id) } : {})}
              highlightId={highlight}
              starred={menu.starred}
              {...(selected ? {} : { toolbar: menu.toolbar })}
            />
          </ContentProvider>
        </div>
      </div>

      {around?.has_after && (
        <div className="pointer-events-none flex flex-none justify-center pb-2">
          <Button size="sm" className="pointer-events-auto shadow-menu" onClick={jumpToLatest}>
            Jump to latest
          </Button>
        </div>
      )}

      {!selected && <UnknownSenderBar chat={chat} meId={meId} />}
      {!selected && posting && <ScheduledBar items={scheduled} onOpen={() => setScheduledOpen(true)} />}

      {selected ? (
        <section
          aria-label="Selected messages"
          className="flex flex-none flex-wrap items-center gap-2 border-t border-line bg-linen px-4 py-3"
        >
          <span role="status" className="min-w-0 flex-1 text-[15px] font-semibold">
            {selected.length === 1 ? "1 message selected" : `${selected.length} messages selected`}
          </span>
          <Button
            variant="outline"
            size="sm"
            disabled={!chosenSent.length}
            onClick={() => finishSelecting(() => menu.forward(chosenSent))}
          >
            Forward
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!chosen.length}
            onClick={() => finishSelecting(() => menu.copy(chosen))}
          >
            Copy
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!chosenSent.length}
            onClick={() => finishSelecting(() => menu.star(chosenSent))}
          >
            Star
          </Button>
          <Button
            variant="outline"
            size="sm"
            disabled={!chosenSent.length}
            onClick={() => finishSelecting(() => menu.hide(chosenSent))}
          >
            Delete for me
          </Button>
          {askable && (
            <Button
              variant="outline"
              size="sm"
              disabled={!chosen.length}
              onClick={() => {
                askAbout(chosen);
                setSelected(null);
              }}
            >
              Ask Queravel
            </Button>
          )}
          {chosenSavable.length > 0 && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => finishSelecting(() => menu.saveToPenielly(chosenSavable))}
            >
              Save to Penielly
            </Button>
          )}
          {emailable && (
            <Button
              size="sm"
              disabled={!chosen.length}
              onClick={() => {
                setEmailFor(chosen);
                setSelected(null);
              }}
            >
              Email
            </Button>
          )}
          <Button variant="ghost" size="sm" onClick={() => setSelected(null)}>
            Cancel
          </Button>
        </section>
      ) : posting ? (
        <MessageBox
          key={chatId}
          ref={box}
          chat={chat}
          draftKey={chatId}
          desktop={desktop}
          placeholder={desktop ? `Message ${title}` : "Message"}
          messages={messages}
          replyTo={replyTo}
          onReplyTo={setReplyTo}
          editing={editing}
          onEditing={startEdit}
          onAttach={() => setSheet("attach")}
          onFiles={chatMedia.attach}
          onAskQueravel={askQueravel}
          schedulable={schedulable}
          media={chatMedia.composer}
          hint={desktop ? keyHint(askable) : undefined}
          onSent={() => {
            atBottom.current = true;
          }}
          onTyping={(on) => client.typing(chatId, on)}
          toast={setToast}
        />
      ) : (
        <p className="m-0 flex-none border-t border-line bg-linen px-5 py-4 text-center text-sm text-ink-secondary">
          Only admins can send messages in this group.
        </p>
      )}

      {toast && (
        <div className="pointer-events-none absolute right-0 bottom-28 left-0 z-50 flex justify-center px-4">
          <Toast>{toast}</Toast>
        </div>
      )}

      {menu.overlays}

      {emailFor && (
        <EmailComposer
          open
          chat={chat}
          chatTitle={view.title}
          messages={emailFor}
          meId={meId}
          onClose={() => setEmailFor(null)}
          onDone={(note) => {
            setEmailFor(null);
            setToast(note);
          }}
        />
      )}
      <PinsSheet
        open={pinsOpen}
        chat={chat}
        pins={pins}
        meId={meId}
        canPin={canPin}
        onUnpin={(id) => void client.unpin(chatId, id).catch((e: Error) => setToast(e.message))}
        onClose={() => setPinsOpen(false)}
      />
      <ScheduledSheet
        open={scheduledOpen}
        items={scheduled}
        onSendNow={(id) => void client.sendScheduledNow(id).catch((e: Error) => setToast(e.message))}
        onCancel={(id) => void client.cancelScheduled(id).catch((e: Error) => setToast(e.message))}
        onReschedule={(id, at) => void client.reschedule(id, at).catch((e: Error) => setToast(e.message))}
        onClose={() => setScheduledOpen(false)}
      />
      {chatMedia.overlays}
    </div>
  );
}
